import { useEffect, useMemo, useState } from 'react'
import { defaultFilter } from 'cmdk'
import { Loader2, RotateCw, SquarePen, Users } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command'
import { useClientLookup } from '@/entities/client/model/queries'
import type { LookupClientResponse } from '@/entities/client/model/types'
import {
  CHAT_COPY,
  chatErrorMessage,
  useChatCapabilities,
  useChatDirectory,
  useChatUiStore,
  useCreateDirect,
  useStartClientConversation,
  type DirectoryEntry,
} from '@/entities/chat'
import { NewGroupDialog } from './NewGroupDialog'
import { PersonAvatar } from './PersonAvatar'

const EMPTY_DIRECTORY_ENTRIES: DirectoryEntry[] = []
const EMPTY_CLIENTS: LookupClientResponse[] = []

function PeopleRows({
  search,
  disabled,
  pendingUserId,
  onAvailableChange,
  onPick,
}: {
  search: string
  disabled: boolean
  pendingUserId: number | null
  onAvailableChange: (values: string[]) => void
  onPick: (entry: DirectoryEntry) => void
}) {
  const directoryQuery = useChatDirectory(search, true)

  useEffect(() => {
    void directoryQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const entries = useMemo(() => directoryQuery.data ?? EMPTY_DIRECTORY_ENTRIES, [directoryQuery.data])

  useEffect(() => {
    onAvailableChange(directoryQuery.isSuccess ? entries.map((entry) => `user:${entry.userId}`) : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, directoryQuery.isSuccess])

  if (directoryQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-ink-4" />
      </div>
    )
  }

  if (directoryQuery.isError) {
    return (
      <button
        type="button"
        onClick={() => void directoryQuery.refetch()}
        className="flex w-full items-center justify-center gap-1.5 px-3 py-4 text-[12.5px] text-ink-3 hover:bg-hover"
      >
        <RotateCw className="h-3.5 w-3.5" />
        {CHAT_COPY.directoryFailed}
      </button>
    )
  }

  if (entries.length === 0) {
    return (
      <p className="px-3 py-4 text-center text-[12.5px] text-ink-3">
        {search.trim() === '' ? CHAT_COPY.newChatEmptyNone : CHAT_COPY.newChatEmpty}
      </p>
    )
  }

  return (
    <>
      {entries.map((entry) => (
        <CommandItem
          key={entry.userId}
          value={`user:${entry.userId}`}
          disabled={disabled}
          onSelect={() => onPick(entry)}
        >
          <PersonAvatar name={entry.name} photoUrl={entry.photoUrl} photoFileId={entry.photoFileId} size="sm" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{entry.name}</span>
            <span className="truncate text-[11.5px] text-ink-4">{entry.employeeType}</span>
          </div>
          {pendingUserId === entry.userId && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-ink-4" />}
        </CommandItem>
      ))}
    </>
  )
}

function ClientRows({
  search,
  disabled,
  pendingClientId,
  onAvailableChange,
  onPick,
}: {
  search: string
  disabled: boolean
  pendingClientId: number | null
  onAvailableChange: (values: string[]) => void
  onPick: (clientId: number) => void
}) {
  const { data: clients, isLoading, isError, isSuccess, refetch } = useClientLookup()

  useEffect(() => {
    void refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const term = search.trim()
  const filtered = useMemo(() => {
    const source = clients ?? EMPTY_CLIENTS
    if (!term) return source
    return source
      .map((client) => ({ client, score: defaultFilter(client.name, term) }))
      .filter((row) => row.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((row) => row.client)
  }, [clients, term])

  useEffect(() => {
    onAvailableChange(isSuccess ? filtered.map((client) => `client:${client.id}`) : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, isSuccess])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-ink-4" />
      </div>
    )
  }

  if (isError) {
    return (
      <button
        type="button"
        onClick={() => void refetch()}
        className="flex w-full items-center justify-center gap-1.5 px-3 py-4 text-[12.5px] text-ink-3 hover:bg-hover"
      >
        <RotateCw className="h-3.5 w-3.5" />
        {CHAT_COPY.clientsFailed}
      </button>
    )
  }

  if (filtered.length === 0) {
    return (
      <p className="px-3 py-4 text-center text-[12.5px] text-ink-3">
        {term === '' ? CHAT_COPY.startConversationEmptyNone : CHAT_COPY.startConversationEmpty}
      </p>
    )
  }

  return (
    <>
      {filtered.map((client) => (
        <CommandItem
          key={client.id}
          value={`client:${client.id}`}
          disabled={disabled}
          onSelect={() => onPick(client.id)}
        >
          {client.name}
          {pendingClientId === client.id && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-ink-4" />}
        </CommandItem>
      ))}
    </>
  )
}

export function NewChatButton({
  variant = 'full',
  onOpened,
}: {
  variant?: 'full' | 'icon'
  onOpened: (conversationId: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [activeValue, setActiveValue] = useState('')
  const [peopleValues, setPeopleValues] = useState<string[]>([])
  const [clientValues, setClientValues] = useState<string[]>([])
  const { canChat, canOps } = useChatCapabilities()
  const createDirect = useCreateDirect()
  const startClient = useStartClientConversation()
  const setTriage = useChatUiStore((state) => state.setTriage)

  const isPending = createDirect.isPending || startClient.isPending

  const availableValues = useMemo(() => {
    const values: string[] = []
    if (canChat && search.trim() === '') values.push('new-group')
    if (canChat) values.push(...peopleValues)
    if (canOps) values.push(...clientValues)
    return values
  }, [canChat, canOps, search, peopleValues, clientValues])

  useEffect(() => {
    if (!availableValues.includes(activeValue)) {
      setActiveValue(availableValues[0] ?? '')
    }
  }, [availableValues, activeValue])

  const closeAndOpen = (conversationId: number) => {
    setTriage('all')
    setOpen(false)
    onOpened(conversationId)
  }

  const handlePickPerson = (entry: DirectoryEntry) => {
    setError(null)
    createDirect.mutate(
      { userId: entry.userId },
      {
        onSuccess: (conversation) => closeAndOpen(conversation.id),
        onError: (mutationError) => setError(chatErrorMessage(mutationError, CHAT_COPY.startDirectFailed)),
      },
    )
  }

  const handlePickClient = (clientId: number) => {
    setError(null)
    startClient.mutate(
      { clientId },
      {
        onSuccess: (conversation) => closeAndOpen(conversation.id),
        onError: (mutationError) => setError(chatErrorMessage(mutationError, CHAT_COPY.startConversationFailed)),
      },
    )
  }

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) {
            setError(null)
            setSearch('')
            setActiveValue('')
            setPeopleValues([])
            setClientValues([])
          }
        }}
      >
        <PopoverTrigger asChild>
          {variant === 'icon' ? (
            <Button variant="ghost" size="icon-sm" aria-label={CHAT_COPY.newChat}>
              <SquarePen size={15} />
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="justify-start">
              <SquarePen size={14} />
              {CHAT_COPY.newChat}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command shouldFilter={false} value={activeValue} onValueChange={setActiveValue}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={CHAT_COPY.newChatSearch}
              className="h-9"
            />
            <CommandList className="max-h-[320px]">
              {canChat && search.trim() === '' && (
                <CommandGroup>
                  <CommandItem
                    value="new-group"
                    onSelect={() => {
                      setOpen(false)
                      setGroupDialogOpen(true)
                    }}
                  >
                    <Users size={14} />
                    {CHAT_COPY.newGroup}
                  </CommandItem>
                </CommandGroup>
              )}
              {canChat && (
                <CommandGroup heading={CHAT_COPY.newChatPeople}>
                  <PeopleRows
                    search={search}
                    disabled={isPending}
                    pendingUserId={createDirect.isPending ? (createDirect.variables?.userId ?? null) : null}
                    onAvailableChange={setPeopleValues}
                    onPick={handlePickPerson}
                  />
                </CommandGroup>
              )}
              {canOps && (
                <CommandGroup heading={CHAT_COPY.newChatClients}>
                  <ClientRows
                    search={search}
                    disabled={isPending}
                    pendingClientId={startClient.isPending ? (startClient.variables?.clientId ?? null) : null}
                    onAvailableChange={setClientValues}
                    onPick={handlePickClient}
                  />
                </CommandGroup>
              )}
            </CommandList>
          </Command>
          {error && <p className="border-t border-line px-3 py-2 text-[11.5px] text-bad">{error}</p>}
        </PopoverContent>
      </Popover>
      <NewGroupDialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen} onCreated={closeAndOpen} />
    </>
  )
}
